import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export type CreditPricingKey = {
  tool: 'floorplan' | 'staging';
  versionType: 'Floorplan2D' | 'Floorplan3D' | 'Empty' | 'Staged';
  eventType: 'first' | 'refinement';
};

export type CreditPricing = {
  floorplan: {
    Floorplan2D: { first: number; refinement: number };
    Floorplan3D: { first: number; refinement: number };
  };
  staging: {
    Empty: { first: number; refinement: number };
    Staged: { first: number; refinement: number };
  };
};

const SEED_DEFAULTS: CreditPricing = {
  floorplan: {
    Floorplan2D: { first: 5, refinement: 1 },
    Floorplan3D: { first: 3, refinement: 1 },
  },
  staging: {
    Empty: { first: 1, refinement: 0.5 },
    Staged: { first: 1, refinement: 0.5 },
  },
};

/**
 * Fetches the active credit_pricing rows once on mount and exposes a nested
 * lookup structure. Values fall back to Phase-F seed defaults if the fetch
 * fails (keeps UI responsive during transient DB outages).
 */
export function useCreditPricing(): { pricing: CreditPricing; isLoading: boolean } {
  const [pricing, setPricing] = useState<CreditPricing>(SEED_DEFAULTS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('credit_pricing')
        .select('tool, version_type, event_type, credit_cost, effective_from')
        .lte('effective_from', new Date().toISOString())
        .order('effective_from', { ascending: false });

      if (cancelled) return;
      if (error || !data) {
        console.warn('[useCreditPricing] fetch failed, using seed defaults:', error?.message);
        setIsLoading(false);
        return;
      }

      // Group by (tool, version_type, event_type); first row wins (most recent effective_from).
      const result: CreditPricing = {
        floorplan: {
          Floorplan2D: { first: 5, refinement: 1 },
          Floorplan3D: { first: 3, refinement: 1 },
        },
        staging: {
          Empty: { first: 1, refinement: 0.5 },
          Staged: { first: 1, refinement: 0.5 },
        },
      };
      const seen = new Set<string>();
      for (const row of data) {
        const key = `${row.tool}/${row.version_type}/${row.event_type}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const et = row.event_type as 'first' | 'refinement';
        if (et !== 'first' && et !== 'refinement') continue;
        if (row.tool === 'floorplan') {
          const vt = row.version_type as 'Floorplan2D' | 'Floorplan3D';
          if (vt === 'Floorplan2D' || vt === 'Floorplan3D') {
            result.floorplan[vt][et] = Number(row.credit_cost);
          }
        } else if (row.tool === 'staging') {
          const vt = row.version_type as 'Empty' | 'Staged';
          if (vt === 'Empty' || vt === 'Staged') {
            result.staging[vt][et] = Number(row.credit_cost);
          }
        }
      }
      setPricing(result);
      setIsLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return { pricing, isLoading };
}
